import {round2, chargeableKg, volumetricKg as sharedVolumetricKg} from '../weightMath'

// round2 and chargeableKg are weightMath's own — Section 0 extracted the
// max(dead, vol) invariant precisely so Catalogue and Packing cannot
// disagree on it, so they are re-exported here unchanged rather than
// redefined. Do not add a second round2/chargeableKg to this file.
export {round2, chargeableKg}

/**
 * l x b x h / RULES.VOLUMETRIC_DIVISOR, to 2dp — a thin wrapper around
 * weightMath's own `volumetricKg`, which does that arithmetic. The divisor,
 * the multiplication, and the rounding step are single-sourced there; this
 * file only re-declares the one thing that is genuinely different.
 *
 * WARNING — this export shares a NAME with weightMath's `volumetricKg` but
 * NOT its missing-dims contract: weightMath's version returns 0 for a
 * material with no dimensions (its own test asserts exactly that, because
 * Packing's cascade needs a number to feed straight into `chargeableKg`).
 * Catalogue's table needs to tell "no dimensions on file" (a flyer/paper
 * wrap) apart from "computes to exactly zero", so THIS one returns `null`
 * instead — a distinct, intentional contract, locked by this file's own
 * test. Import the one your caller actually needs; autocomplete will
 * happily hand you either.
 *
 * This wrapper guards only on the ABSENCE of dims (`!dims`), not on
 * `dims.length !== 3` the way the pre-extraction code here used to. That
 * means it now also accepts object-form `{l, b, h}` dims, same as
 * weightMath's — a deliberate choice: nothing in this dataset uses
 * object-form dims today (every `PACKS[*].dims` is array-or-null), but
 * there was never a real reason for Catalogue to reject a shape Packing
 * accepts, and silently rejecting it was itself an undocumented gap. The
 * null sentinel is preserved for "no dims at all"; anything else is handed
 * straight to the shared implementation.
 */
export function volumetricKg(dims) {
  if (!dims) return null
  return sharedVolumetricKg(dims)
}

/**
 * Modal charged weight of a `[[kg, count], …]` histogram.
 * Ties break to the LOWER weight — deliberate, and asserted by the
 * dataset's own test suite. A tie broken upward would quietly argue for a
 * heavier freeze.
 */
export function modalWeight(hist) {
  let bestW = 0
  let bestN = -1
  ;(hist || []).forEach(([w, n]) => {
    if (n > bestN || (n === bestN && w < bestW)) {
      bestW = w
      bestN = n
    }
  })
  return bestN < 0 ? 0 : bestW
}

/**
 * Share of shipments NOT at the modal weight, to one decimal.
 * The denominator is the shipment count passed in, not the histogram total
 * — they agree in this dataset, but the shipment count is the figure the
 * merchant is shown, so it is the one that must govern.
 */
export function spreadPct(hist, ships) {
  const total = Number(ships) || 0
  if (!total || !hist || !hist.length) return 0
  const top = hist.reduce((n, [, c]) => Math.max(n, c), 0)
  return Math.round(((total - top) / total) * 1000) / 10
}

/**
 * Combine a set of SKU records the way a merge does: histograms ADD, the
 * most probable weight is the mode of the combined histogram, and dead
 * weight is the shipment-weighted mean — never a plain average of the
 * rows' dead weights. Nothing here averages a modal weight; that would
 * produce a weight the courier never actually billed.
 */
export function combine(list) {
  const rows = list || []
  const buckets = new Map()
  let ships = 0
  let weightedDead = 0

  rows.forEach((s) => {
    const n = s.ships || 0
    ships += n
    weightedDead += (s.deadKg || 0) * n
    ;(s.hist || []).forEach(([w, c]) => buckets.set(w, (buckets.get(w) || 0) + c))
  })

  const hist = Array.from(buckets.entries()).sort((a, b) => a[0] - b[0])
  const first = rows[0]

  return {
    ships,
    hist,
    mpw: hist.length ? modalWeight(hist) : (first ? first.mpw : 0),
    spread: spreadPct(hist, ships),
    dead: ships ? round2(weightedDead / ships) : (first ? first.deadKg : 0),
  }
}

/**
 * One rolled-up view per SKU, keyed by code. A mother carries its
 * children's numbers; everything else reports its own. The table's Dead
 * weight and Shipments columns sort on THESE values, so materialise the map
 * once per render and hand cells the entry — never recompute inside a
 * comparator.
 */
export function rollUp(skus) {
  const rows = skus || []
  const byCode = new Map(rows.map((s) => [s.sku, s]))
  const out = {}

  rows.forEach((s) => {
    const kids = (s.motherOf || []).map((c) => byCode.get(c)).filter(Boolean)
    if (!kids.length) {
      out[s.sku] = {
        sku: s.sku, kids: [], rolled: false,
        ships: s.ships, mpw: s.mpw, spread: s.spread, dead: s.deadKg, added: 0,
      }
      return
    }
    const c = combine([s].concat(kids))
    out[s.sku] = {
      sku: s.sku, kids, rolled: true,
      ships: c.ships, mpw: c.mpw, spread: c.spread, dead: c.dead,
      added: c.ships - s.ships,
    }
  })

  return out
}

/**
 * Selecting a SKU that is already a mother pulls its children along, so a
 * merge always ends in ONE flat mother with every code under it. This walks
 * each requested code as a pre-order (depth-first) visit — a code is
 * emitted immediately, followed by all of ITS children, before moving on to
 * the next requested code — so a later sibling in `codes` never gets
 * interleaved ahead of an earlier code's own children. A `seen` set (not
 * just "already in `out`") guards against re-visiting and against a cycle
 * in a hand-edited dataset; today's data only ever goes one level deep
 * (applyMerge flattens children so a child's own motherOf is cleared), but
 * this does not assume that.
 */
export function expandSelection(skus, codes) {
  const byCode = new Map((skus || []).map((s) => [s.sku, s]))
  const out = []
  const seen = new Set()

  function visit(code) {
    if (seen.has(code)) return
    seen.add(code)
    out.push(code)
    const s = byCode.get(code)
    ;(s && s.motherOf ? s.motherOf : []).forEach(visit)
  }

  ;(codes || []).forEach(visit)

  return out
}

/** Human label for a packing-material id, by lookup in a `packs` list. 'Not set' is the mockup's own wording for an unmapped SKU. */
export function packLabel(packs, packId) {
  const p = (packs || []).find((x) => x.id === packId)
  return p ? p.label : 'Not set'
}

/**
 * The four figures Catalogue counts over the whole SKU list: how many codes are
 * on file, how many arrived automatically and nobody has looked at yet, which
 * ones carry a code that cannot be used, and how many report to a mother.
 *
 * Extracted out of useCatalogueState's `counters` (which still adds `shipped`
 * from the merchant record on top) because the stat strip is no longer rendered
 * by the page that owns that hook — Package master's container draws it above
 * the tab bar, from `catalogueStatCards` in CataloguePage.js. Two derivations of
 * the same four numbers would be free to disagree, and the strip and the page's
 * own banners quote them side by side.
 *
 * `badCodes` is the LIST, not a length: the banner on Catalogue renders the
 * offending rows, and the card only wants how many there are.
 */
export function catalogueCounters(skus) {
  const rows = skus || []
  return {
    total: rows.length,
    autoPending: rows.filter((s) => s.source === 'auto' && !s.reviewed).length,
    badCodes: rows.filter((s) => s.dq),
    mapped: rows.filter((s) => s.childOf).length,
  }
}
